import React from 'react';
import type { LayoutDirection } from '../../utils/diagramKits';
import { GENERAL_SHAPES_SERVER_ENABLED } from '../../constants/featureFlags';
import { useOnboardingStore } from '../../stores/onboardingStore';

type ThemeMode = 'dark' | 'light';
type IconStyle = 'badge' | 'solid';
type ColorPickerStyle = 'wheel' | 'palette';
type SocketLabelVisibility = 'always' | 'hover' | 'hidden';
type EdgeRoutingMode = 'bezier' | 'orthogonal';

interface SettingsPanelProps {
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
  };
  glass: React.CSSProperties;
  isOpen: boolean;
  theme: ThemeMode;
  iconStyle: IconStyle;
  colorPickerStyle?: ColorPickerStyle;
  autoOpenInfoOnHover?: boolean;
  socketLabelVisibility?: SocketLabelVisibility;
  defaultRoutingMode?: EdgeRoutingMode;
  /** Name of the page the page-level controls below act on. */
  pageName?: string;
  /** The page's own routing choice, or undefined when it follows the default. */
  pageRoutingMode?: EdgeRoutingMode;
  /** The page's own label multiplier, or undefined when it follows the default. */
  pageTextScale?: number;
  forceLayoutEdges?: boolean;
  tintEdgesByDestination?: boolean;
  layoutDirection?: LayoutDirection;
  mermaidSubgraphMode?: 'frames' | 'groups';
  mermaidNodeStyle?: 'nodes' | 'shapes';
  generalShapesEnabled?: boolean;
  stickyDrawColorEnabled?: boolean;
  lastDrawColor?: string | null;
  sequenceMessageNumbering?: boolean;
  sequenceMonochromeViewer?: boolean;
  sequenceMonochromePaper?: 'white' | 'black';
  onClose: () => void;
  onThemeChange: (theme: ThemeMode) => void;
  onIconStyleChange: (style: IconStyle) => void;
  onColorPickerStyleChange: (style: ColorPickerStyle) => void;
  onAutoOpenInfoOnHoverChange: (enabled: boolean) => void;
  onSocketLabelVisibilityChange: (vis: SocketLabelVisibility) => void;
  onDefaultRoutingModeChange: (mode: EdgeRoutingMode) => void;
  /** `undefined` clears the override and puts the page back on the default. */
  onPageRoutingModeChange: (mode: EdgeRoutingMode | undefined) => void;
  onPageTextScaleChange: (scale: number | undefined) => void;
  onForceLayoutEdgesChange: (enabled: boolean) => void;
  onTintEdgesByDestinationChange: (enabled: boolean) => void;
  onLayoutDirectionChange: (direction: LayoutDirection) => void;
  onMermaidSubgraphModeChange: (mode: 'frames' | 'groups') => void;
  onMermaidNodeStyleChange: (style: 'nodes' | 'shapes') => void;
  onGeneralShapesEnabledChange: (enabled: boolean) => void;
  onStickyDrawColorEnabledChange: (enabled: boolean) => void;
  onSequenceMessageNumberingChange: (enabled: boolean) => void;
  onSequenceMonochromeViewerChange: (enabled: boolean) => void;
  onSequenceMonochromePaperChange: (paper: 'white' | 'black') => void;
  onExportSettings: () => void;
  onImportSettings: () => void;
  onClearDiagram: () => void;
  onResetViewport: () => void;
}

export function SettingsPanel({
  palette,
  glass,
  isOpen,
  theme,
  iconStyle,
  colorPickerStyle = 'wheel',
  autoOpenInfoOnHover = true,
  socketLabelVisibility = 'always',
  defaultRoutingMode = 'bezier',
  pageName,
  pageRoutingMode,
  pageTextScale,
  forceLayoutEdges = false,
  tintEdgesByDestination = true,
  layoutDirection = 'auto',
  mermaidSubgraphMode = 'frames',
  mermaidNodeStyle = 'nodes',
  generalShapesEnabled = false,
  stickyDrawColorEnabled = false,
  lastDrawColor = null,
  sequenceMessageNumbering = true,
  sequenceMonochromeViewer = false,
  sequenceMonochromePaper = 'white',
  onClose,
  onThemeChange,
  onIconStyleChange,
  onColorPickerStyleChange,
  onAutoOpenInfoOnHoverChange,
  onSocketLabelVisibilityChange,
  onDefaultRoutingModeChange,
  onPageRoutingModeChange,
  onPageTextScaleChange,
  onForceLayoutEdgesChange,
  onTintEdgesByDestinationChange,
  onLayoutDirectionChange,
  onMermaidSubgraphModeChange,
  onMermaidNodeStyleChange,
  onGeneralShapesEnabledChange,
  onStickyDrawColorEnabledChange,
  onSequenceMessageNumberingChange,
  onSequenceMonochromeViewerChange,
  onSequenceMonochromePaperChange,
  onExportSettings,
  onImportSettings,
  onClearDiagram,
  onResetViewport,
}: SettingsPanelProps) {
  const nudgesEnabled = useOnboardingStore(s => s.nudgesEnabled);
  const setNudgesEnabled = useOnboardingStore(s => s.setNudgesEnabled);
  const startTutorial = useOnboardingStore(s => s.startTutorial);

  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div
        className="w-96 max-h-[85vh] flex flex-col rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden"
        style={glass}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: palette.panelBorder }}
        >
          <span style={{ color: palette.panelText }} className="text-sm font-bold">
            Settings
          </span>
          <button
            onClick={onClose}
            style={{ color: palette.panelSubText }}
            className="transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
          {/* Theme */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Theme
            </label>
            <div className="flex gap-2">
              {(['dark', 'light'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => onThemeChange(t)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: theme === t ? '#4f46e5' : 'transparent',
                    borderColor: theme === t ? '#4f46e5' : palette.panelBorder,
                    color: theme === t ? 'white' : palette.panelText,
                  }}
                >
                  {t === 'dark' ? '🌙 Dark' : '☀️ Light'}
                </button>
              ))}
            </div>
          </div>

          {/* Icon style */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Node Icon Style
            </label>
            <div className="flex gap-2">
              {(['badge', 'solid'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => onIconStyleChange(s)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: iconStyle === s ? '#4f46e5' : 'transparent',
                    borderColor: iconStyle === s ? '#4f46e5' : palette.panelBorder,
                    color: iconStyle === s ? 'white' : palette.panelText,
                  }}
                >
                  {s === 'badge' ? '⬤ Badge' : '◈ Solid'}
                </button>
              ))}
            </div>
          </div>

          {/* Sticky draw color */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Sticky draw color
            </label>
            <div className="flex gap-2">
              {[true, false].map(enabled => (
                <button
                  key={enabled ? 'on' : 'off'}
                  onClick={() => onStickyDrawColorEnabledChange(enabled)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: stickyDrawColorEnabled === enabled ? '#4f46e5' : 'transparent',
                    borderColor: stickyDrawColorEnabled === enabled ? '#4f46e5' : palette.panelBorder,
                    color: stickyDrawColorEnabled === enabled ? 'white' : palette.panelText,
                  }}
                >
                  {enabled ? 'On' : 'Off'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              When on, new shapes use the last color you picked (color picker or c then c then 0–9).
              {lastDrawColor ? (
                <> Current: <span className="inline-block w-2.5 h-2.5 rounded-sm align-middle mx-1" style={{ background: lastDrawColor }} /> {lastDrawColor}</>
              ) : null}
            </p>
          </div>
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Color Picker Style
            </label>
            <div className="flex gap-2">
              {(['wheel', 'palette'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => onColorPickerStyleChange(s)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: colorPickerStyle === s ? '#4f46e5' : 'transparent',
                    borderColor: colorPickerStyle === s ? '#4f46e5' : palette.panelBorder,
                    color: colorPickerStyle === s ? 'white' : palette.panelText,
                  }}
                >
                  {s === 'wheel' ? '🎡 Wheel' : '🎨 Palette'}
                </button>
              ))}
            </div>
          </div>

          {/* Hover info panel behavior */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Hover Info Tab
            </label>
            <div className="flex gap-2">
              {[true, false].map(enabled => (
                <button
                  key={enabled ? 'on' : 'off'}
                  onClick={() => onAutoOpenInfoOnHoverChange(enabled)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: autoOpenInfoOnHover === enabled ? '#4f46e5' : 'transparent',
                    borderColor: autoOpenInfoOnHover === enabled ? '#4f46e5' : palette.panelBorder,
                    color: autoOpenInfoOnHover === enabled ? 'white' : palette.panelText,
                  }}
                >
                  {enabled ? 'On' : 'Off'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Temporarily switch to Info tab while hovering canvas items.
            </p>
          </div>

          {/* Sequence */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Sequence
            </label>
            <div className="space-y-3">
              <div>
                <div className="text-[11px] mb-1.5" style={{ color: palette.panelText }}>Message numbering</div>
                <div className="flex gap-2">
                  {[true, false].map(enabled => (
                    <button
                      key={enabled ? 'on' : 'off'}
                      onClick={() => onSequenceMessageNumberingChange(enabled)}
                      className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                      style={{
                        background: sequenceMessageNumbering === enabled ? '#4f46e5' : 'transparent',
                        borderColor: sequenceMessageNumbering === enabled ? '#4f46e5' : palette.panelBorder,
                        color: sequenceMessageNumbering === enabled ? 'white' : palette.panelText,
                      }}
                    >
                      {enabled ? 'On' : 'Off'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] mb-1.5" style={{ color: palette.panelText }}>Black and white viewer</div>
                <div className="flex gap-2">
                  {[true, false].map(enabled => (
                    <button
                      key={enabled ? 'on' : 'off'}
                      onClick={() => onSequenceMonochromeViewerChange(enabled)}
                      className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                      style={{
                        background: sequenceMonochromeViewer === enabled ? '#4f46e5' : 'transparent',
                        borderColor: sequenceMonochromeViewer === enabled ? '#4f46e5' : palette.panelBorder,
                        color: sequenceMonochromeViewer === enabled ? 'white' : palette.panelText,
                      }}
                    >
                      {enabled ? 'On' : 'Off'}
                    </button>
                  ))}
                </div>
              </div>
              {sequenceMonochromeViewer && (
                <div>
                  <div className="text-[11px] mb-1.5" style={{ color: palette.panelText }}>Paper</div>
                  <div className="flex gap-2">
                    {(['white', 'black'] as const).map(paper => (
                      <button
                        key={paper}
                        onClick={() => onSequenceMonochromePaperChange(paper)}
                        className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                        style={{
                          background: sequenceMonochromePaper === paper ? '#4f46e5' : 'transparent',
                          borderColor: sequenceMonochromePaper === paper ? '#4f46e5' : palette.panelBorder,
                          color: sequenceMonochromePaper === paper ? 'white' : palette.panelText,
                        }}
                      >
                        {paper === 'white' ? 'White' : 'Black'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Socket label visibility */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Socket Labels
            </label>
            <div className="flex gap-2">
              {(['always', 'hover', 'hidden'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => onSocketLabelVisibilityChange(v)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: socketLabelVisibility === v ? '#4f46e5' : 'transparent',
                    borderColor: socketLabelVisibility === v ? '#4f46e5' : palette.panelBorder,
                    color: socketLabelVisibility === v ? 'white' : palette.panelText,
                  }}
                >
                  {v === 'always' ? 'Always' : v === 'hover' ? 'Hover' : 'Hidden'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Show socket names always, only on hover, or hide completely.
            </p>
          </div>

          {/* Default edge routing */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Default Edge Routing
            </label>
            <div className="flex gap-2">
              {(['bezier', 'orthogonal'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => onDefaultRoutingModeChange(m)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: defaultRoutingMode === m ? '#4f46e5' : 'transparent',
                    borderColor: defaultRoutingMode === m ? '#4f46e5' : palette.panelBorder,
                    color: defaultRoutingMode === m ? 'white' : palette.panelText,
                  }}
                >
                  {m === 'bezier' ? 'Bezier' : 'Orthogonal'}
                </button>
              ))}
            </div>
          </div>

          {/* ── This page ─────────────────────────────────────────
              Page overrides beat the app defaults above and are beaten in
              turn by anything set on a single edge, so a diagram can read
              differently from the rest of the workspace without every item
              on it having to say so. */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Edge Routing{pageName ? ` \u00b7 ${pageName}` : ' \u00b7 This Page'}
            </label>
            <div className="flex gap-2">
              {([undefined, 'bezier', 'orthogonal'] as const).map(m => (
                <button
                  key={m ?? 'default'}
                  onClick={() => onPageRoutingModeChange(m)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: pageRoutingMode === m ? '#4f46e5' : 'transparent',
                    borderColor: pageRoutingMode === m ? '#4f46e5' : palette.panelBorder,
                    color: pageRoutingMode === m ? 'white' : palette.panelText,
                  }}
                >
                  {m === undefined ? 'Default' : m === 'bezier' ? 'Bezier' : 'Orthogonal'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              How this page draws its edges. An edge you have set yourself keeps its own shape.
            </p>
          </div>

          {/* Page text size */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Text Size{pageName ? ` \u00b7 ${pageName}` : ' \u00b7 This Page'}
            </label>
            <div className="flex gap-2">
              {([
                { value: 0.9, label: 'S' },
                { value: undefined, label: 'Default' },
                { value: 1.1, label: 'L' },
                { value: 1.25, label: 'XL' },
              ] as const).map(step => (
                <button
                  key={step.label}
                  onClick={() => onPageTextScaleChange(step.value)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: pageTextScale === step.value ? '#4f46e5' : 'transparent',
                    borderColor: pageTextScale === step.value ? '#4f46e5' : palette.panelBorder,
                    color: pageTextScale === step.value ? 'white' : palette.panelText,
                  }}
                >
                  {step.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Scales node, frame and edge labels. Boxes keep the size they were drawn at, so a long label can clip; an import fits its boxes to this setting.
            </p>
          </div>

          {/* Force layout edges */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Edge Force Layout
            </label>
            <div className="flex gap-2">
              {[true, false].map(enabled => (
                <button
                  key={enabled ? 'on' : 'off'}
                  onClick={() => onForceLayoutEdgesChange(enabled)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: forceLayoutEdges === enabled ? '#4f46e5' : 'transparent',
                    borderColor: forceLayoutEdges === enabled ? '#4f46e5' : palette.panelBorder,
                    color: forceLayoutEdges === enabled ? 'white' : palette.panelText,
                  }}
                >
                  {enabled ? 'On' : 'Off'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Nudge a bezier wire only when it crosses a nearby node. Displacement is capped so edges stay readable.
            </p>
          </div>

          {/* Which way Beautify ranks the diagram */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Beautify Direction
            </label>
            <div className="flex gap-2">
              {(['auto', 'ttb', 'ltr'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => onLayoutDirectionChange(v)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: (layoutDirection ?? 'auto') === v ? '#4f46e5' : 'transparent',
                    borderColor: (layoutDirection ?? 'auto') === v ? '#4f46e5' : palette.panelBorder,
                    color: (layoutDirection ?? 'auto') === v ? 'white' : palette.panelText,
                  }}
                >
                  {v === 'auto' ? 'Auto' : v === 'ttb' ? '↓ Down' : '→ Across'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Which way Beautify ranks the diagram. Auto follows the kit — a sequence downward, an architecture map across. Pick one yourself when the drawing reads better the other way.
            </p>
          </div>

          {/* Colour wires by where they land */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Edge Destination Colour
            </label>
            <div className="flex gap-2">
              {[true, false].map(enabled => (
                <button
                  key={enabled ? 'on' : 'off'}
                  onClick={() => onTintEdgesByDestinationChange(enabled)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: tintEdgesByDestination === enabled ? '#4f46e5' : 'transparent',
                    borderColor: tintEdgesByDestination === enabled ? '#4f46e5' : palette.panelBorder,
                    color: tintEdgesByDestination === enabled ? 'white' : palette.panelText,
                  }}
                >
                  {enabled ? 'On' : 'Off'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Tint a wire with the colour of where it ends up: the frame it arrives in, or the busy node it arrives at when there is no frame. Wires between two quiet nodes, and wires you have coloured yourself, are left alone.
            </p>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Mermaid Subgraphs
            </label>
            <div className="flex gap-2">
              {([
                { id: 'frames' as const, label: 'Frames' },
                { id: 'groups' as const, label: 'Groups' },
              ]).map(option => (
                <button
                  key={option.id}
                  onClick={() => onMermaidSubgraphModeChange(option.id)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: mermaidSubgraphMode === option.id ? '#4f46e5' : 'transparent',
                    borderColor: mermaidSubgraphMode === option.id ? '#4f46e5' : palette.panelBorder,
                    color: mermaidSubgraphMode === option.id ? 'white' : palette.panelText,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Frames keep subgraph contents visible like Mermaid. Groups collapse them into architecture subgraphs.
            </p>
          </div>

          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Mermaid flowchart import
            </label>
            <div className="flex flex-col gap-2">
              {([
                { id: 'nodes' as const, label: 'Architecture nodes', hint: 'Service, Database, Person, Queue' },
                { id: 'shapes' as const, label: 'Drawing shapes', hint: 'Box, oval, diamond, cylinder' },
              ]).map(option => (
                <button
                  key={option.id}
                  onClick={() => onMermaidNodeStyleChange(option.id)}
                  className="w-full py-2 px-3 rounded-lg text-left text-sm font-medium transition-all border"
                  style={{
                    background: mermaidNodeStyle === option.id ? '#4f46e5' : 'transparent',
                    borderColor: mermaidNodeStyle === option.id ? '#4f46e5' : palette.panelBorder,
                    color: mermaidNodeStyle === option.id ? 'white' : palette.panelText,
                  }}
                >
                  <span className="block">{option.label}</span>
                  <span
                    className="block text-[11px] font-normal mt-0.5"
                    style={{ color: mermaidNodeStyle === option.id ? 'rgba(255,255,255,0.8)' : palette.panelSubText }}
                  >
                    {option.hint}
                  </span>
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
              Applies when importing a Mermaid flowchart. Sequence diagrams are unchanged.
            </p>
          </div>

          {/* General Shapes — only shown if server allows */}
          {GENERAL_SHAPES_SERVER_ENABLED && (
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-2"
                style={{ color: palette.panelSubText }}
              >
                General Shapes
              </label>
              <div className="flex gap-2">
                {[true, false].map(enabled => (
                  <button
                    key={enabled ? 'on' : 'off'}
                    onClick={() => onGeneralShapesEnabledChange(enabled)}
                    className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                    style={{
                      background: generalShapesEnabled === enabled ? '#4f46e5' : 'transparent',
                      borderColor: generalShapesEnabled === enabled ? '#4f46e5' : palette.panelBorder,
                      color: generalShapesEnabled === enabled ? 'white' : palette.panelText,
                    }}
                  >
                    {enabled ? 'On' : 'Off'}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px]" style={{ color: palette.panelSubText }}>
                Show extra geometric shapes (cross) in the Draw palette.
                Oval, rounded, diamond, cylinder, hexagon, and triangle are always available.
              </p>
            </div>
          )}

          {/* Onboarding */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Learning
            </label>
            <div className="flex gap-2 mb-2">
              {[true, false].map(enabled => (
                <button
                  key={enabled ? 'on' : 'off'}
                  onClick={() => setNudgesEnabled(enabled)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-all border"
                  style={{
                    background: nudgesEnabled === enabled ? '#4f46e5' : 'transparent',
                    borderColor: nudgesEnabled === enabled ? '#4f46e5' : palette.panelBorder,
                    color: nudgesEnabled === enabled ? 'white' : palette.panelText,
                  }}
                >
                  {enabled ? 'Shortcut nudges on' : 'Off'}
                </button>
              ))}
            </div>
            <p className="mb-2 text-[11px]" style={{ color: palette.panelSubText }}>
              Tiny corner hints when you click something that has a keyboard shortcut. Never blocks the canvas.
            </p>
            <button
              onClick={() => { startTutorial(); onClose(); }}
              className="w-full py-2 rounded-lg text-sm font-medium transition-colors border"
              style={{ borderColor: palette.panelBorder, color: palette.panelText }}
            >
              Restart product tour
            </button>
          </div>

          {/* Export / Import */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2"
              style={{ color: palette.panelSubText }}
            >
              Settings Data
            </label>
            <div className="flex gap-2">
              <button
                onClick={onExportSettings}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors border"
                style={{ borderColor: palette.panelBorder, color: palette.panelText }}
              >
                ↓ Export
              </button>
              <button
                onClick={onImportSettings}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors border"
                style={{ borderColor: palette.panelBorder, color: palette.panelText }}
              >
                ↑ Import
              </button>
            </div>
          </div>

          {/* Danger Zone */}
          <div>
            <label
              className="block text-xs uppercase tracking-wider mb-2 text-red-400"
            >
              Danger Zone
            </label>
            <div className="flex gap-2">
              <button
                onClick={onClearDiagram}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors border border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                Clear Diagram
              </button>
              <button
                onClick={onResetViewport}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors border"
                style={{ borderColor: palette.panelBorder, color: palette.panelText }}
              >
                Reset View
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
